import { SoundButton } from "../../../../common/SoundButton/SoundButton";
import type { ApiSocialUserSummary } from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { haptics } from "../../../../../logic/helpers/haptics";
import { useFriendsManagement } from "../../../../../logic/hooks/useFriendsManagement";
import { useFriendInviteSharing } from "../../../../../logic/hooks/useSocialFriendInvites";
import { SimpleDialog } from "../../../../common/SimpleDialog/SimpleDialog";
import { SimpleDialogHeader } from "../../../../common/SimpleDialog/comps/SimpleDialogHeader";
import "./FriendsDialog.scss";

interface FriendsDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

function initials(user: ApiSocialUserSummary) {
    const named = [user.firstName, user.lastName]
        .filter((value): value is string => Boolean(value))
        .map((value) => value.slice(0, 1))
        .join("");
    if (named) return named.toUpperCase();
    return user.displayName
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.slice(0, 1))
        .join("")
        .toUpperCase();
}

function FriendAvatar({ user }: { user: ApiSocialUserSummary }) {
    return (
        <span className="friends-dialog__avatar" aria-hidden="true">
            <span>{initials(user)}</span>
            {user.avatarUrl && (
                <img
                    src={user.avatarUrl}
                    alt=""
                    draggable={false}
                    loading="lazy"
                    onError={(event) => {
                        event.currentTarget.style.display = "none";
                    }}
                />
            )}
        </span>
    );
}

export function FriendsDialog({ isOpen, onClose }: FriendsDialogProps) {
    const { t } = useTranslation();
    const { state: authState } = useSlice("authSlice");
    const isAuthReady = authState.bootstrapStatus === "ready";
    const management = useFriendsManagement(isOpen && isAuthReady);
    const inviteSharing = useFriendInviteSharing({
        enabled: isOpen && isAuthReady,
        userId: authState.userId,
    });
    const [confirmingRemoval, setConfirmingRemoval] = useState<string | null>(
        null,
    );
    const incomingInvites = useMemo(
        () =>
            management.invites.filter(
                (invite) => invite.direction === "INCOMING",
            ),
        [management.invites],
    );
    const outgoingInvites = useMemo(
        () =>
            management.invites.filter(
                (invite) => invite.direction === "OUTGOING",
            ),
        [management.invites],
    );
    const titleId = "friends-dialog-title";
    const isMutating = management.mutatingId !== null;

    useEffect(() => {
        if (!isOpen) setConfirmingRemoval(null);
    }, [isOpen]);

    const accept = async (inviteId: string) => {
        haptics.impact("light");
        const succeeded = await management.acceptInvite(inviteId);
        haptics.notification(succeeded ? "success" : "error");
    };

    const decline = async (inviteId: string) => {
        haptics.impact("light");
        const succeeded = await management.declineInvite(inviteId);
        if (!succeeded) haptics.notification("error");
    };

    const revoke = async (inviteId: string) => {
        haptics.impact("light");
        const succeeded = await management.revokeInvite(inviteId);
        if (!succeeded) haptics.notification("error");
    };

    const remove = async (friendUserId: string) => {
        haptics.impact("medium");
        const succeeded = await management.removeFriend(friendUserId);
        if (succeeded) setConfirmingRemoval(null);
        haptics.notification(succeeded ? "success" : "error");
    };

    return (
        <SimpleDialog
            className="friends-dialog"
            isOpen={isOpen}
            onClose={onClose}
            aria-labelledby={titleId}
            header={
                <SimpleDialogHeader
                    title={t("friends.title")}
                    titleId={titleId}
                    closeLabel={t("friends.close")}
                    onClose={onClose}
                />
            }
        >
            <div className="friends-dialog__body">
                <SoundButton
                    type="button"
                    className="friends-dialog__add"
                    disabled={!isAuthReady}
                    onClick={() => {
                        haptics.impact("medium");
                        void inviteSharing.share();
                    }}
                >
                    <span aria-hidden="true">+</span>
                    {t("friends.add")}
                </SoundButton>

                {!isAuthReady && (
                    <p className="friends-dialog__state">
                        {t("friends.unavailable")}
                    </p>
                )}

                {(management.error || inviteSharing.error) && (
                    <div className="friends-dialog__state" role="alert">
                        <p>{management.error ?? inviteSharing.error}</p>
                        {management.status === "error" && (
                            <SoundButton
                                type="button"
                                onClick={() => void management.refresh()}
                            >
                                {t("friends.retry")}
                            </SoundButton>
                        )}
                    </div>
                )}

                {management.status === "loading" &&
                    management.friends.length === 0 &&
                    management.invites.length === 0 && (
                        <p className="friends-dialog__state" role="status">
                            {t("friends.loading")}
                        </p>
                    )}

                {incomingInvites.length > 0 && (
                    <section className="friends-dialog__section">
                        <h3>{t("friends.requests")}</h3>
                        <ul>
                            {incomingInvites.map((invite) => {
                                const user = invite.otherUser;
                                if (!user) return null;
                                const isAccepting =
                                    management.mutatingId ===
                                    `accept:${invite.inviteId}`;
                                const isDeclining =
                                    management.mutatingId ===
                                    `decline:${invite.inviteId}`;
                                return (
                                    <li key={invite.inviteId}>
                                        <FriendAvatar user={user} />
                                        <span className="friends-dialog__name">
                                            {user.displayName}
                                        </span>
                                        <span className="friends-dialog__actions">
                                            <SoundButton
                                                type="button"
                                                className="friends-dialog__action friends-dialog__action--primary"
                                                disabled={isMutating}
                                                onClick={() =>
                                                    void accept(invite.inviteId)
                                                }
                                            >
                                                {isAccepting
                                                    ? t("friends.working")
                                                    : t("friends.accept")}
                                            </SoundButton>
                                            <SoundButton
                                                type="button"
                                                className="friends-dialog__action"
                                                disabled={isMutating}
                                                onClick={() =>
                                                    void decline(invite.inviteId)
                                                }
                                            >
                                                {isDeclining
                                                    ? t("friends.working")
                                                    : t("friends.decline")}
                                            </SoundButton>
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                )}

                {outgoingInvites.length > 0 && (
                    <section className="friends-dialog__section">
                        <h3>{t("friends.sentRequests")}</h3>
                        <ul>
                            {outgoingInvites.map((invite) => {
                                const user = invite.otherUser;
                                const isCurrent =
                                    management.mutatingId ===
                                    `revoke:${invite.inviteId}`;
                                return (
                                    <li key={invite.inviteId}>
                                        {user ? (
                                            <FriendAvatar user={user} />
                                        ) : (
                                            <span
                                                className="friends-dialog__avatar"
                                                aria-hidden="true"
                                            >
                                                <span>+</span>
                                            </span>
                                        )}
                                        <span className="friends-dialog__name">
                                            {user?.displayName ??
                                                t("friends.inviteLink")}
                                        </span>
                                        <span className="friends-dialog__actions">
                                            <SoundButton
                                                type="button"
                                                className="friends-dialog__action"
                                                disabled={isMutating}
                                                onClick={() =>
                                                    void revoke(invite.inviteId)
                                                }
                                            >
                                                {isCurrent
                                                    ? t("friends.working")
                                                    : t("friends.revoke")}
                                            </SoundButton>
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                )}

                {management.inviteCursor && (
                    <SoundButton
                        type="button"
                        className="friends-dialog__load-more"
                        disabled={management.loadingMore !== null}
                        onClick={() => void management.loadMoreInvites()}
                    >
                        {management.loadingMore === "invites"
                            ? t("friends.loadingMore")
                            : t("friends.loadMoreRequests")}
                    </SoundButton>
                )}

                <section className="friends-dialog__section">
                    <h3>
                        {t("friends.yourFriends", {
                            count: management.friends.length,
                        })}
                    </h3>
                    {management.status === "ready" &&
                    management.friends.length === 0 ? (
                        <p className="friends-dialog__empty">
                            {t("friends.empty")}
                        </p>
                    ) : (
                        <ul>
                            {management.friends.map((friend) => {
                                const friendUserId = friend.user.userId;
                                const isCurrent =
                                    management.mutatingId ===
                                    `remove:${friendUserId}`;
                                const isConfirming =
                                    confirmingRemoval === friendUserId;
                                return (
                                    <li key={friend.friendshipId}>
                                        <FriendAvatar user={friend.user} />
                                        <span className="friends-dialog__name">
                                            {friend.user.displayName}
                                        </span>
                                        <span className="friends-dialog__actions">
                                            {isConfirming ? (
                                                <>
                                                    <SoundButton
                                                        type="button"
                                                        className="friends-dialog__action friends-dialog__action--danger"
                                                        disabled={isMutating}
                                                        onClick={() =>
                                                            void remove(
                                                                friendUserId,
                                                            )
                                                        }
                                                    >
                                                        {isCurrent
                                                            ? t(
                                                                  "friends.working",
                                                              )
                                                            : t(
                                                                  "friends.confirmRemove",
                                                              )}
                                                    </SoundButton>
                                                    <SoundButton
                                                        type="button"
                                                        className="friends-dialog__action"
                                                        disabled={isMutating}
                                                        onClick={() =>
                                                            setConfirmingRemoval(
                                                                null,
                                                            )
                                                        }
                                                    >
                                                        {t("friends.cancel")}
                                                    </SoundButton>
                                                </>
                                            ) : (
                                                <SoundButton
                                                    type="button"
                                                    className="friends-dialog__action"
                                                    disabled={isMutating}
                                                    onClick={() =>
                                                        setConfirmingRemoval(
                                                            friendUserId,
                                                        )
                                                    }
                                                >
                                                    {t("friends.remove")}
                                                </SoundButton>
                                            )}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>

                {management.friendCursor && (
                    <SoundButton
                        type="button"
                        className="friends-dialog__load-more"
                        disabled={management.loadingMore !== null}
                        onClick={() => void management.loadMoreFriends()}
                    >
                        {management.loadingMore === "friends"
                            ? t("friends.loadingMore")
                            : t("friends.loadMoreFriends")}
                    </SoundButton>
                )}
            </div>
        </SimpleDialog>
    );
}
